import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { LINKS } from "@/lib/data";
import { useMPNav } from "@/components/Header";
import { SHOP_ENABLED } from "@/lib/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIALS = [
  { label: "Whatnot", href: LINKS.whatnot },
  { label: "TikTok", href: LINKS.tiktok },
  { label: "eBay", href: LINKS.ebay },
  { label: "Shopify Store", href: LINKS.shopify },
  { label: "Instagram", href: LINKS.instagram },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const go = useMPNav();

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("You're on the list. See you at the next drop.");
      setEmail("");
    } catch {
      toast.error("Please enter a valid email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="relative bg-[#050505] pt-24 md:pt-32 pb-10 border-t border-white/10">
      <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 pb-20">
          <div className="lg:col-span-6">
            <h3 className="font-display font-extrabold uppercase tracking-tighter text-white text-3xl md:text-4xl leading-tight max-w-lg">
              Never miss a <span className="text-[#3FE1D1]">drop</span>.
            </h3>
            <p className="mt-4 text-white/55 max-w-md">
              Get show times, grail alerts and consignment openings straight to your inbox.
            </p>
            <form onSubmit={subscribe} className="mt-6 flex max-w-md">
              <input
                data-testid="newsletter-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="flex-1 bg-transparent border border-[#262629] border-r-0 text-white placeholder:text-white/30 px-4 py-3.5 text-sm focus:outline-none focus:border-[#3FE1D1]"
              />
              <button
                data-testid="newsletter-submit"
                disabled={loading}
                className="bg-[#3FE1D1] text-[#050505] font-bold uppercase text-xs tracking-widest px-6 hover:bg-white transition-colors disabled:opacity-60"
              >
                Join
              </button>
            </form>
          </div>

          <div className="lg:col-span-3">
            <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-5">Where to find us</p>
            <ul className="space-y-3">
              {SOCIALS.map((s) => (
                <li key={s.label}>
                  <a
                    data-testid={`footer-${s.label.toLowerCase().split(" ")[0]}`}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer"
                    className="group inline-flex items-center gap-1.5 text-white/70 hover:text-[#3FE1D1] transition-colors"
                  >
                    {s.label}
                    <ArrowUpRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-5">Explore</p>
            <ul className="space-y-3">
              {[
                ...(SHOP_ENABLED ? [{ l: "Shop", to: "/shop" }] : []),
                { l: "Where We Sell", hash: "sell" },
                ...(SHOP_ENABLED ? [{ l: "Collections", hash: "collections" }] : []),
                { l: "Consign With Us", to: "/consign" },
              ].map((n) => (
                <li key={n.l}>
                  <button
                    onClick={() => go(n)}
                    className="text-white/70 hover:text-[#3FE1D1] transition-colors"
                  >
                    {n.l}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Giant wordmark */}
        <div className="border-t border-white/10 pt-10">
          <div className="font-display font-extrabold uppercase tracking-tighter leading-none text-[16vw] lg:text-[13vw] text-center select-none">
            <span className="text-white">MONTHLY</span>
            <span className="text-[#3FE1D1]">POPS</span>
          </div>
          <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-white/40 text-xs">
            <p>© {new Date().getFullYear()} Monthly Pops. The premier autograph destination.</p>
            <p>6 years live on Whatnot · 100,000+ items sold</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
